import { plateFont, numFont } from '../../../utils/fonts';
import React from 'react';
import { Avatar, Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import StatusBadge, { statusConfig } from '../../shared/StatusBadge';
import { useWidgetFrame } from '../frameContext';
import { bodyHeightPx } from '../sizes';
import { avatarColor } from './format';

// Data rows got 8 px more air than the design's 40 px: two-line rows read cramped at 40.
const ROW_H = 48;
// Chip filter row and the "Näytä lisää" footer keep the design's 40 px.
const BAR_H = 40;
const TONES = {
  neutral: { bg: '#f8fafc', color: '#64748b' },
  amber: { bg: '#fef3e2', color: '#92400e' },
  red: { bg: '#fee2e2', color: '#c53030' },
  accent: { bg: 'rgba(49,130,206,0.10)', color: '#3182ce' },
  green: { bg: '#e6ffed', color: '#2f8c54' },
};

export function Chip({ label, tone = 'neutral' }) {
  const colors = TONES[tone] || TONES.neutral;
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', px: 0.9, height: 20, borderRadius: '10px', fontSize: 11.5, fontWeight: 600, bgcolor: colors.bg, color: colors.color, whiteSpace: 'nowrap', flexShrink: 0 }}>
      {label}
    </Box>
  );
}

function ActionButton({ action, testId }) {
  if (!action) return null;
  const variant = action.variant === 'primary' ? 'contained' : action.variant === 'quiet' ? 'text' : 'outlined';
  return (
    <Button
      size="small"
      variant={variant}
      color={action.variant === 'primary' ? 'secondary' : action.color || 'inherit'}
      disabled={action.busy}
      data-testid={testId}
      onClick={(event) => {
        event.stopPropagation();
        action.onClick?.();
      }}
      sx={{
        minWidth: 0,
        px: 1.25,
        py: 0.25,
        fontSize: 12,
        fontWeight: 600,
        borderRadius: '8px',
        lineHeight: 1.6,
        whiteSpace: 'nowrap',
        ...(variant === 'outlined' ? { borderColor: '#e2e8f0', color: '#1a202c' } : {}),
        ...(variant === 'text' ? { color: '#64748b' } : {}),
      }}
    >
      {action.label}
    </Button>
  );
}

function RowLead({ row }) {
  if (row.avatar) {
    return (
      <Avatar sx={{ width: 26, height: 26, fontSize: 11, fontWeight: 700, bgcolor: avatarColor(row.avatarIdx || 0) }}>
        {row.avatar}
      </Avatar>
    );
  }
  if (row.icon) {
    const Icon = row.icon;
    return <Icon sx={{ fontSize: 18, color: row.iconColor || '#94a3b8', flexShrink: 0 }} />;
  }
  const state = statusConfig[row.state] || null;
  return <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: state ? state.color : '#cbd5e1', flexShrink: 0 }} />;
}

/**
 * List / action list body (design templates 3 and 4). Rows are 48 px on desktop; capacity is
 * derived from the widget size and a 40 px "Näytä lisää (N)" footer is reserved when needed.
 */
export default function ListBody({ rows = [], chips, activeChip, onChip, summary, totalCount, onMore, testIdPrefix }) {
  const { t } = useTranslation();
  const frame = useWidgetFrame();
  const size = frame?.size || 'M';
  const mobile = Boolean(frame?.isMobile);
  const headless = Boolean(frame?.headless);

  let capacity = rows.length;
  if (!mobile) {
    const available = bodyHeightPx(size, !headless) - (chips ? BAR_H : 0) - (summary ? 36 : 0);
    capacity = Math.max(1, Math.floor(available / ROW_H));
    const total = totalCount ?? rows.length;
    if (total > capacity) capacity = Math.max(1, Math.floor((available - BAR_H) / ROW_H));
  } else {
    capacity = Math.min(rows.length, 8);
  }
  const total = totalCount ?? rows.length;
  const visible = rows.slice(0, capacity);
  const hiddenCount = Math.max(0, total - visible.length);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {chips ? (
        <Box sx={{ height: BAR_H, display: 'flex', alignItems: 'center', gap: 0.75, px: 2.25, borderBottom: '1px solid #f1f5f9', overflowX: 'auto', flexShrink: 0 }}>
          {chips.map((chip) => {
            const active = chip.key === activeChip;
            const state = chip.state ? statusConfig[chip.state] : null;
            return (
              <Box
                key={chip.key}
                component="button"
                type="button"
                data-testid={testIdPrefix ? `${testIdPrefix}-chip-${chip.key}` : undefined}
                onClick={() => onChip?.(chip.key)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.6,
                  px: 1.1,
                  height: 24,
                  borderRadius: '12px',
                  border: 0,
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  bgcolor: active ? '#1a365d' : '#f8fafc',
                  color: active ? '#fff' : '#64748b',
                  whiteSpace: 'nowrap',
                }}
              >
                {state && !active ? <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: state.color }} /> : null}
                {chip.label}
                {chip.n != null ? ` (${chip.n})` : ''}
              </Box>
            );
          })}
        </Box>
      ) : null}
      {summary ? (
        <Box data-testid={testIdPrefix ? `${testIdPrefix}-summary` : undefined} sx={{ minHeight: 36, display: 'flex', alignItems: 'center', gap: 1, px: 2.25, fontSize: 12.5, color: 'text.secondary', borderBottom: '1px solid #f1f5f9', flexShrink: 0, flexWrap: 'wrap' }}>
          {summary}
        </Box>
      ) : null}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {visible.map((row, index) => {
          const clickable = Boolean(row.onClick) && frame?.mode !== 'edit';
          const stack = mobile && (row.action || (row.secondary && row.right));
          return (
            <Box
              key={row.key ?? index}
              data-testid={row.testId}
              onClick={clickable ? row.onClick : undefined}
              sx={{
                display: 'flex',
                alignItems: stack ? 'flex-start' : 'center',
                flexDirection: stack ? 'column' : 'row',
                gap: stack ? 0.5 : 1.5,
                minHeight: ROW_H,
                px: 2.25,
                py: stack ? 1 : 0,
                borderBottom: index < visible.length - 1 ? '1px solid #f1f5f9' : 0,
                borderLeft: row.highlight ? '4px solid #f59e0b' : 0,
                bgcolor: row.highlight ? 'rgba(245, 158, 11, 0.05)' : 'transparent',
                cursor: clickable ? 'pointer' : 'default',
                '&:hover': clickable ? { bgcolor: '#f8fafc' } : {},
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1, width: stack ? '100%' : undefined }}>
                <RowLead row={row} />
                {row.time ? (
                  <Typography sx={{ ...numFont, fontSize: 13, width: 42, flexShrink: 0, color: 'text.secondary' }}>{row.time}</Typography>
                ) : null}
                {row.plate ? (
                  <Typography sx={{ ...plateFont, fontSize: 12.5, fontWeight: 700, color: '#1a365d', width: 66, flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.plate}
                  </Typography>
                ) : null}
                <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                  <Typography noWrap sx={{ fontSize: 13.5, fontWeight: row.bold ? 700 : 500, color: 'text.primary' }}>
                    {row.primary}
                  </Typography>
                  {row.secondary ? (
                    <Typography noWrap sx={{ fontSize: 12, color: 'text.secondary' }}>
                      {row.secondary}
                    </Typography>
                  ) : null}
                </Box>
                {row.pill ? <StatusBadge status={row.pill} size="small" /> : null}
                {row.chip ? <Chip label={row.chip} tone={row.chipTone} /> : null}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, alignSelf: stack ? 'flex-end' : 'center' }}>
                {row.right != null ? (
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: row.rightTone === 'muted' ? 400 : 600,
                      ...(row.rightMono === false ? {} : numFont),
                      color: row.rightTone === 'red' ? '#c53030' : row.rightTone === 'muted' ? 'text.secondary' : 'text.primary',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.right}
                  </Typography>
                ) : null}
                <ActionButton action={row.action2} testId={row.action2?.testId} />
                <ActionButton action={row.action} testId={row.action?.testId} />
              </Box>
            </Box>
          );
        })}
      </Box>
      {hiddenCount > 0 ? (
        <Box
          component="button"
          type="button"
          onClick={onMore}
          sx={{ height: BAR_H, flexShrink: 0, border: 0, borderTop: '1px solid #f1f5f9', bgcolor: 'transparent', color: 'secondary.main', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: onMore ? 'pointer' : 'default' }}
        >
          {t('dashboard.actions.showMore', { n: hiddenCount })}
        </Box>
      ) : null}
    </Box>
  );
}

export function SummaryText({ parts }) {
  return parts.map((part, index) => (
    <React.Fragment key={index}>
      {index > 0 ? <span>·</span> : null}
      {part.strong ? (
        <Box component="strong" sx={{ color: 'text.primary', ...(part.mono ? numFont : {}) }}>
          {part.text}
        </Box>
      ) : (
        <span>{part.text}</span>
      )}
    </React.Fragment>
  ));
}

export function SummaryAction({ label, onClick, testId }) {
  return (
    <Typography
      component="button"
      type="button"
      data-testid={testId}
      onClick={onClick}
      sx={{ ml: 'auto', background: 'none', border: 0, p: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: 'secondary.main', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
    >
      {label}
    </Typography>
  );
}
